import {
  Link,
  StatusIndicator,
  TableProps,
  Button,
  Popover,
} from '@cloudscape-design/components';
import { Question } from 'src/types/helios/Question';
import { ExportFileConfig } from 'src/utils/exportFileUtils';
import { formatDateTime } from 'src/utils/generalUtils';
import { useState } from 'react';
import {
  getQuestionContent,
  getQuestionDetailsUrl,
  openQuestionDetailsPage,
} from 'src/helper/questionHelper';
import { NavigateFunction } from 'react-router-dom';

// Define default visible columns
export const defaultVisibleColumns = ['questionId', 'division', 'title', 'status'];

export const tagCell = (item: Question) => {
  const allTags = Object.entries(item.tagsIncluded).flatMap(([tagGroupName, tagValues]) =>
    tagValues.map((data, index) => ({
      key: `${tagGroupName}-${index}`.replace(/[^a-zA-Z0-9-]/g, '-'),
      label: `${tagGroupName}: ${data}`,
    })),
  );
  const remainingCount = allTags.length - 2;

  return (
    <div className="tag-container">
      {allTags.slice(0, 2).map(({ key, label }) => (
        <span key={key} className="tag">
          {label}
        </span>
      ))}
      {remainingCount > 0 && (
        <Popover
          size="medium"
          position="top"
          triggerType="custom"
          content={
            <div className="tag-popover-container">
              {allTags.map(({ key, label }) => (
                <span key={key} className="tag">
                  {label}
                </span>
              ))}
            </div>
          }
        >
          <Button variant="inline-link">+{remainingCount} more</Button>
        </Popover>
      )}
    </div>
  );
};
export const getQuestionsTableDefinition = (
  navigate: NavigateFunction,
  selectedLanguage?: string,
): TableProps.ColumnDefinition<Question>[] => {
  return [
    {
      id: 'questionId',
      header: 'ID',
      cell: (item: Question) => (
        <span className={item.isExcludedFromTemplate ? 'excluded-question' : ''}>
          {item.questionId}
        </span>
      ),
      width: 80,
    },
    {
      id: 'division',
      header: 'Division',
      cell: (item: Question) => (
        <span className={item.isExcludedFromTemplate ? 'excluded-question' : ''}>
          {item.divisionName}
        </span>
      ),
      width: 120,
    },
    {
      id: 'title',
      header: 'Overview',
      cell: (item: Question) => (
        <div
          className={`title-container ${item.isExcludedFromTemplate ? 'excluded-question' : ''}`}
        >
          <Button
            ariaLabel="View Question details (opens new tab)"
            href={getQuestionDetailsUrl(item)}
            iconAlign="right"
            iconName="external"
            target="_blank"
            variant="icon"
          ></Button>
          <span
            className="title-hover"
            onClick={() => {
              openQuestionDetailsPage(navigate, item);
            }}
          >
            {getQuestionContent(item, selectedLanguage).questionDetails.title}
          </span>
        </div>
      ),

      maxWidth: 700,
      minWidth: 500,
    },
    {
      id: 'tags',
      header: 'Tags',
      cell: (item: Question) => (
        <div className={item.isExcludedFromTemplate ? 'excluded-question' : ''}>
          {tagCell(item)}
        </div>
      ),
      maxWidth: 400,
    },
    {
      id: 'Categories',
      header: 'Categories',
      cell: (item: Question) => (
        <span className={item.isExcludedFromTemplate ? 'excluded-question' : ''}>
          {item.categories && item.categories.length > 0
            ? item.categories.join(', ')
            : '-'}
        </span>
      ),
      maxWidth: 300,
    },
    {
      id: 'subQuestions',
      header: 'Sub Questions',
      cell: (item: Question) => (
        <span className={item.isExcludedFromTemplate ? 'excluded-question' : ''}>
          {item.localizedContent.content.subQuestions?.length || 0}
        </span>
      ),
      width: 90,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item: Question) => (
        <div className={item.isExcludedFromTemplate ? 'excluded-question' : ''}>
          <StatusIndicator type={item.isActive ? 'success' : 'error'}>
            {item.isActive ? 'Active' : 'Inactive'}
          </StatusIndicator>
        </div>
      ),
      width: 80,
    },
    {
      id: 'createdBy',
      header: 'Created By',
      cell: (item: Question) => (
        <span className={item.isExcludedFromTemplate ? 'excluded-question' : ''}>
          {item.createdBy || '-'}
        </span>
      ),
      width: 110,
    },
    {
      id: 'createdAt',
      header: 'Created At',
      cell: (item: Question) => (
        <span className={item.isExcludedFromTemplate ? 'excluded-question' : ''}>
          {formatDateTime(item.createdAt)}
        </span>
      ),
      width: 130,
    },
    {
      id: 'lastUpdatedBy',
      header: 'Updated By',
      cell: (item: Question) => (
        <span className={item.isExcludedFromTemplate ? 'excluded-question' : ''}>
          {item.lastUpdatedBy || '-'}
        </span>
      ),
      width: 110,
    },
    {
      id: 'lastUpdatedAt',
      header: 'Updated At',
      cell: (item: Question) => (
        <span className={item.isExcludedFromTemplate ? 'excluded-question' : ''}>
          {formatDateTime(item.lastUpdatedAt)}
        </span>
      ),
      width: 130,
    },
  ].filter((col) => col.id !== undefined);
};

export const exportQuestionsConfig: ExportFileConfig = {
  fileName: 'questions_list.csv',
  mappings: {
    'ID': (question: Question) => question.questionId,
    'Overview': (question: Question) =>
      question.localizedContent.content.questionDetails.title,
    'Division': (question: Question) => question.divisionName,
    'Categories': (question: Question) =>
      question.categories ? question.categories.join(', ') : '-',
    'Tags Included': (question: Question) =>
      Object.entries(question.tagsIncluded || [])
        .map(([tagGroupName, tagValues]) => {
          return tagValues.map((data) => `${tagGroupName} - ${data}`);
        })
        .join(', '),

    'Sub Questions': (question: Question) =>
      (question.localizedContent.content.subQuestions?.length || 0).toString(),
    'Status': (question: Question) => (question.isActive ? 'Active' : 'Inactive'),

    // English (en-US) Content Fields
    'Language': (question: Question) => question.localizedContent.language || 'en-US',

    'Created By': (question: Question) => question.createdBy || '-',
    'Created At': (question: Question) => formatDateTime(question.createdAt),
    'Updated At': (question: Question) => formatDateTime(question.lastUpdatedAt),
    'Updated By': (question: Question) => question.lastUpdatedBy || '-',
  },
};
